// src/EmpresaCadastro.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from './api';
import { recarregar } from './dados';
import { PageHeader, Card, Campo, Botao, Busca, Aviso, Avatar, Vazio, inputCls, IconPredio, IconMais, IconLapis, IconLixo, IconBusca, IconUsuarios, IconTelefone } from './ui';

// Texto para comparação: minúsculo e sem acentos
const normalizar = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const EmpresaCadastro = ({ empresas, usuarios = [], setCurrentView, currentUser, onCompanyCreated }) => {
    const [formData, setFormData] = useState({
        idFirebase: '',
        nrEmpresaSequencial: '',
        nome: '',
        contatoNome: '',
        contatoTelefone: '',
        contatoSetor: '',
        ativo: true,
    });
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [aviso, setAviso] = useState(null);
    const fecharAviso = useCallback(() => setAviso(null), []);

    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    // ---- busca de empresa para editar (filtra enquanto digita) ----
    const [buscaEmpresa, setBuscaEmpresa] = useState('');
    const [mostrarOpcoes, setMostrarOpcoes] = useState(false);
    const [destaque, setDestaque] = useState(0);
    const listaOpcoesRef = useRef(null);

    const opcoesEmpresa = useMemo(() => {
        const termo = normalizar(buscaEmpresa);
        if (!termo) return empresas;
        const comeca = [];
        const contem = [];
        empresas.forEach(emp => {
            const nome = normalizar(emp.nome);
            if (nome.startsWith(termo)) comeca.push(emp);
            else if (nome.includes(termo)) contem.push(emp);
        });
        return [...comeca, ...contem];
    }, [empresas, buscaEmpresa]);

    useEffect(() => { setDestaque(0); }, [buscaEmpresa]);

    useEffect(() => {
        const el = listaOpcoesRef.current && listaOpcoesRef.current.children[destaque];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [destaque]);

    // o campo mostra o nome da empresa em edição (ao clicar "Editar" na tabela, ao salvar, etc.)
    useEffect(() => {
        const emp = empresas.find(e => e.idFirebase === selectedCompanyId);
        setBuscaEmpresa(emp ? emp.nome : '');
    }, [selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const escolherEmpresa = (emp) => {
        setSelectedCompanyId(emp.idFirebase);
        setBuscaEmpresa(emp.nome);
        setMostrarOpcoes(false);
    };

    const teclaBusca = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setMostrarOpcoes(true);
            setDestaque(i => Math.min(i + 1, Math.max(0, opcoesEmpresa.length - 1)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setDestaque(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (mostrarOpcoes && opcoesEmpresa[destaque]) escolherEmpresa(opcoesEmpresa[destaque]);
        } else if (e.key === 'Escape') {
            setMostrarOpcoes(false);
        }
    };

    const destacarTermo = (nome) => {
        const termo = normalizar(buscaEmpresa);
        const i = termo ? normalizar(nome).indexOf(termo) : -1;
        if (i < 0) return nome;
        return (<>{nome.slice(0, i)}<b className="text-indigo-700">{nome.slice(i, i + termo.length)}</b>{nome.slice(i + termo.length)}</>);
    };


    // ---- usuários da empresa selecionada (editar / excluir / incluir) ----
    const USUARIO_VAZIO = { nome: '', telefone: '', setor: '', ativo: true };
    const [editUsuarioId, setEditUsuarioId] = useState(null);
    const [editUsuario, setEditUsuario] = useState(USUARIO_VAZIO);
    const [novoUsuario, setNovoUsuario] = useState(USUARIO_VAZIO);
    const [msgUsuarios, setMsgUsuarios] = useState('');
    const [salvandoUsuario, setSalvandoUsuario] = useState(false);

    const usuariosDaEmpresa = useMemo(
        () => (selectedCompanyId ? usuarios.filter(u => u.empresaId === selectedCompanyId) : [])
            .slice().sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || ''))),
        [usuarios, selectedCompanyId]
    );

    // ao trocar de empresa, fecha a edição em andamento
    useEffect(() => {
        setEditUsuarioId(null);
        setNovoUsuario(USUARIO_VAZIO);
        setMsgUsuarios('');
    }, [selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const nomeRepetido = (nome, ignorarId) =>
        usuariosDaEmpresa.some(u => u.idFirebase !== ignorarId && normalizar(u.nome) === normalizar(nome));

    const iniciarEdicaoUsuario = (u) => {
        setEditUsuarioId(u.idFirebase);
        setEditUsuario({ nome: u.nome || '', telefone: u.telefone || '', setor: u.setor || '', ativo: u.ativo !== false });
        setMsgUsuarios('');
    };

    const salvarEdicaoUsuario = async () => {
        const nome = editUsuario.nome.trim().toUpperCase();
        const telefone = editUsuario.telefone.trim().toUpperCase();
        const setor = editUsuario.setor.trim().toUpperCase();
        if (!nome || !telefone) { setMsgUsuarios('Nome e telefone são obrigatórios.'); return; }
        if (nomeRepetido(nome, editUsuarioId)) { setMsgUsuarios('Já existe outro usuário com este nome nesta empresa.'); return; }
        setSalvandoUsuario(true);
        try {
            const r = await api.put(`/usuarios/${editUsuarioId}`, { nome, telefone, setor, ativo: editUsuario.ativo });
            await recarregar('usuarios');
            if (r.pendenciasAtualizadas) recarregar('pendencias');
            setEditUsuarioId(null);
            setMsgUsuarios(`Usuário ${nome} atualizado${r.pendenciasAtualizadas ? ` (e ${r.pendenciasAtualizadas} pendência(s))` : ''}.`);
        } catch (err) {
            console.error('Erro ao alterar usuário:', err);
            setMsgUsuarios(`Erro ao alterar usuário: ${err.message}`);
        } finally {
            setSalvandoUsuario(false);
        }
    };

    const excluirUsuario = async (u) => {
        setMsgUsuarios('');
        try {
            if (!window.confirm(`Excluir o usuário ${u.nome}? Esta ação não pode ser desfeita.`)) return;
            // o servidor recusa se o usuário estiver em alguma pendência e explica o motivo
            await api.del(`/usuarios/${u.idFirebase}`);
            await recarregar('usuarios');
            setMsgUsuarios(`Usuário ${u.nome} excluído.`);
        } catch (err) {
            console.error('Erro ao excluir usuário:', err);
            setMsgUsuarios(err.message);
        }
    };

    const incluirUsuario = async () => {
        const nome = novoUsuario.nome.trim().toUpperCase();
        const telefone = novoUsuario.telefone.trim().toUpperCase();
        const setor = novoUsuario.setor.trim().toUpperCase();
        if (!nome || !telefone) { setMsgUsuarios('Preencha nome e telefone do novo usuário.'); return; }
        if (nomeRepetido(nome)) { setMsgUsuarios('Já existe um usuário com este nome nesta empresa.'); return; }
        setSalvandoUsuario(true);
        try {
            await api.post('/usuarios', { nome, telefone, setor, empresaId: selectedCompanyId });
            await recarregar('usuarios');
            setNovoUsuario(USUARIO_VAZIO);
            setMsgUsuarios(`Usuário ${nome} incluído.`);
        } catch (err) {
            console.error('Erro ao incluir usuário:', err);
            setMsgUsuarios(`Erro ao incluir usuário: ${err.message}`);
        } finally {
            setSalvandoUsuario(false);
        }
    };

    const campoUsuario = (valor, onChange, placeholder, onEnter) => (
        <input
            type="text"
            value={valor}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } if (e.key === 'Escape') setEditUsuarioId(null); }}
            className="w-full h-8 border border-gray-300 rounded-md px-2 text-sm uppercase bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
    );

    const handleNewCompanyForm = useCallback(() => {
        setFormData({
            idFirebase: '',
            nrEmpresaSequencial: '',
            nome: '',
            contatoNome: '',
            contatoTelefone: '',
            contatoSetor: '',
            ativo: true,
        });
        setIsEditing(false);
        setError('');
        setSelectedCompanyId('');
    }, []);



    useEffect(() => {
        if (selectedCompanyId) {
            const companyToEdit = empresas.find(emp => emp.idFirebase === selectedCompanyId);
            if (companyToEdit) {
                setFormData({
                    idFirebase: companyToEdit.idFirebase,
                    nrEmpresaSequencial: companyToEdit.nrEmpresaSequencial || '',
                    nome: companyToEdit.nome || '',
                    contatoNome: companyToEdit.contatoNome || '',
                    contatoTelefone: companyToEdit.contatoTelefone || '',
                    contatoSetor: companyToEdit.contatoSetor || '',
                    ativo: companyToEdit.ativo !== false,
                });
                setIsEditing(true);
                setError('');
            }
            // se ainda não está na lista (acabou de ser criada), mantém o formulário como está
        } else {
            handleNewCompanyForm();
        }
    }, [selectedCompanyId, empresas, handleNewCompanyForm]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Aplica toUpperCase para os campos de texto que devem ser maiúsculas
        setFormData(prev => ({
            ...prev,
            [name]: (name === 'nome' || name === 'contatoNome' || name === 'contatoTelefone' || name === 'contatoSetor') ? value.toUpperCase() : value
        }));
    };

    const handleSaveCompany = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // o contato é exigido só no cadastro de empresa nova; ao editar (ex.: só inativar) basta o nome,
        // porque várias empresas antigas vieram sem contato
        if (!formData.nome || (!isEditing && (!formData.contatoNome || !formData.contatoTelefone))) {
            setError(isEditing ? 'Informe o nome da empresa.' : 'Nome da Empresa, Nome Contato e Telefone Contato são campos obrigatórios.');
            setLoading(false);
            return;
        }

        try {
            const dataToSave = {
                nome: formData.nome,
                contatoNome: formData.contatoNome,
                contatoTelefone: formData.contatoTelefone,
                contatoSetor: formData.contatoSetor,
                ativo: formData.ativo,
            };

            if (isEditing) {
                await api.put(`/empresas/${formData.idFirebase}`, dataToSave);
                await recarregar('empresas');
                recarregar('pendencias');
                setAviso({ tipo: 'ok', texto: `Empresa ${formData.nome} alterada${formData.ativo ? '' : ' (inativa)'}.` });
            } else {
                const criada = await api.post('/empresas', dataToSave);
                await recarregar('empresas');
                setAviso({ tipo: 'ok', texto: `Empresa ${criada.nome} cadastrada. Agora você pode incluir os usuários dela.` });
                setSelectedCompanyId(criada.idFirebase);
                setFormData(prev => ({
                    ...prev,
                    idFirebase: criada.idFirebase,
                    nrEmpresaSequencial: criada.nrEmpresaSequencial
                }));
                setIsEditing(true);
                if (onCompanyCreated) {
                    onCompanyCreated(criada.idFirebase, criada.nrEmpresaSequencial);
                }
            }
        } catch (err) {
            console.error("Erro ao salvar empresa:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCompany = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir esta empresa? Esta ação é irreversível.")) {
            setLoading(true);
            setError('');
            try {
                // o servidor recusa se a empresa tiver usuários ou pendências
                await api.del(`/empresas/${id}`);
                await recarregar('empresas');
                setAviso({ tipo: 'ok', texto: 'Empresa excluída.' });
                handleNewCompanyForm();
            } catch (err) {
                console.error("Erro ao excluir empresa:", err);
                setAviso({ tipo: 'erro', texto: err.message });
            } finally {
                setLoading(false);
            }
        }
    };

    const termoTabela = normalizar(searchTerm);
    const filteredEmpresas = empresas.filter(empresa =>
        !termoTabela ||
        normalizar(empresa.nome).includes(termoTabela) ||
        normalizar(empresa.contatoNome).includes(termoTabela) ||
        String(empresa.contatoTelefone || '').replace(/\D/g, '').includes(searchTerm.replace(/\D/g, '') || '\u0000')
    );

    const empresasInativas = empresas.filter(e => e.ativo === false).length;

    const usuariosPorEmpresa = useMemo(() => {
        const m = {};
        usuarios.forEach(u => { m[u.empresaId] = (m[u.empresaId] || 0) + 1; });
        return m;
    }, [usuarios]);

    return (
        <div className="space-y-4">
            <PageHeader icon={IconPredio} cor="green" titulo="Empresas" subtitulo={`${empresas.length} empresa${empresas.length === 1 ? '' : 's'}${empresasInativas ? ` (${empresasInativas} inativa${empresasInativas === 1 ? '' : 's'})` : ''} · ${usuarios.length} usuário${usuarios.length === 1 ? '' : 's'}`}>
                <Botao variante="primario" onClick={handleNewCompanyForm}><IconMais /> Nova empresa</Botao>
            </PageHeader>

            <Aviso aviso={aviso} onFechar={fecharAviso} />

            <div className="grid grid-cols-1 lg:grid-cols-[24rem_1fr] gap-4 items-start">
                {/* ---------- formulário ---------- */}
                <div className="space-y-4 lg:sticky lg:top-0">
                    <Card titulo="Buscar empresa para editar">
                        <div className="relative">
                            <IconBusca className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                autoComplete="off"
                                value={buscaEmpresa}
                                onChange={(e) => {
                                    setBuscaEmpresa(e.target.value.toUpperCase());
                                    setMostrarOpcoes(true);
                                    if (selectedCompanyId) setSelectedCompanyId('');
                                }}
                                onFocus={(e) => { setMostrarOpcoes(true); e.target.select(); }}
                                onBlur={() => setTimeout(() => setMostrarOpcoes(false), 150)}
                                onKeyDown={teclaBusca}
                                placeholder="Digite o nome da empresa..."
                                className={`w-full h-9 border rounded-md pl-9 pr-8 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-indigo-300 ${selectedCompanyId ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'}`}
                            />
                            {buscaEmpresa && (
                                <button type="button" tabIndex={-1} title="Limpar (nova empresa)"
                                    onMouseDown={(e) => { e.preventDefault(); setBuscaEmpresa(''); setSelectedCompanyId(''); setMostrarOpcoes(true); }}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg leading-none">×</button>
                            )}
                            {mostrarOpcoes && (
                                <ul ref={listaOpcoesRef} className="absolute z-30 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-64 overflow-y-auto text-sm">
                                    {opcoesEmpresa.length > 0 ? opcoesEmpresa.map((emp, i) => (
                                        <li key={emp.idFirebase}
                                            onMouseDown={(e) => { e.preventDefault(); escolherEmpresa(emp); }}
                                            onMouseEnter={() => setDestaque(i)}
                                            className={`px-3 py-1.5 cursor-pointer flex justify-between gap-3 ${i === destaque ? 'bg-indigo-100' : ''} ${emp.idFirebase === selectedCompanyId ? 'font-semibold' : ''}`}>
                                            <span className="truncate">{destacarTermo(emp.nome)}{emp.ativo === false && <span className="ml-1.5 text-[10px] font-semibold uppercase text-gray-500 bg-gray-200 rounded px-1 py-px">inativa</span>}</span>
                                            <span className="text-gray-400 text-xs flex-shrink-0">{usuariosPorEmpresa[emp.idFirebase] || 0} usu.</span>
                                        </li>
                                    )) : (
                                        <li className="px-3 py-2 text-gray-500">Nenhuma empresa encontrada.</li>
                                    )}
                                </ul>
                            )}
                        </div>
                    </Card>

                    <Card titulo={isEditing ? `Editando empresa${formData.nrEmpresaSequencial ? ` · Nr ${formData.nrEmpresaSequencial}` : ''}` : 'Nova empresa'}>
                        <form onSubmit={handleSaveCompany} className="space-y-3">
                            <Campo rotulo="Nome da empresa" obrigatorio>
                                <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} className={`${inputCls} uppercase`} autoComplete="off" />
                            </Campo>
                            <div className="pt-1 text-xs font-semibold text-gray-400 uppercase tracking-wide border-t border-gray-100">Contato principal</div>
                            <Campo rotulo="Nome do contato" obrigatorio={!isEditing}>
                                <input type="text" name="contatoNome" value={formData.contatoNome} onChange={handleInputChange} className={`${inputCls} uppercase`} autoComplete="off" />
                            </Campo>
                            <div className="grid grid-cols-2 gap-3">
                                <Campo rotulo="Telefone" obrigatorio={!isEditing}>
                                    <input type="tel" name="contatoTelefone" value={formData.contatoTelefone} onChange={handleInputChange} className={inputCls} placeholder="47 99999-9999" />
                                </Campo>
                                <Campo rotulo="Setor">
                                    <input type="text" name="contatoSetor" value={formData.contatoSetor} onChange={handleInputChange} className={`${inputCls} uppercase`} />
                                </Campo>
                            </div>
                            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
                                <input type="checkbox" checked={formData.ativo}
                                    onChange={(e) => setFormData(p => ({ ...p, ativo: e.target.checked }))}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                <span>
                                    Ativa
                                    <span className="block text-xs text-gray-500">Desmarcada, a empresa não aparece para escolha em pendências novas. As pendências já abertas não mudam.</span>
                                </span>
                            </label>
                            {error && <p className="text-red-600 text-sm">{error}</p>}
                            <div className="flex gap-2 pt-1">
                                <Botao type="submit" variante="primario" disabled={loading} className="flex-1">
                                    {loading ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Cadastrar empresa'}
                                </Botao>
                                {isEditing && <Botao onClick={handleNewCompanyForm} disabled={loading}>Cancelar</Botao>}
                            </div>
                            {isEditing && (
                                <button type="button" onClick={() => handleDeleteCompany(formData.idFirebase)} disabled={loading}
                                    className="w-full text-xs text-red-600 hover:text-red-800 pt-1">
                                    Excluir esta empresa
                                </button>
                            )}
                        </form>
                    </Card>
                </div>

                {/* ---------- lista: usuários da empresa escolhida, ou todas as empresas ---------- */}
                {selectedCompanyId ? (
                    <Card
                        semPadding
                        titulo={<span className="flex items-center gap-2"><IconUsuarios className="h-4 w-4 text-gray-400" />Usuários de <span className="uppercase">{formData.nome}</span> <span className="text-gray-400 font-normal">({usuariosDaEmpresa.length})</span></span>}
                        acao={<Botao tamanho="sm" variante="secundario" onClick={handleNewCompanyForm}>← Todas as empresas</Botao>}
                    >
                        {msgUsuarios && <div className="mx-4 mt-3 text-sm text-gray-700 bg-gray-100 rounded px-3 py-1.5">{msgUsuarios}</div>}
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 py-2 text-left w-16">Nr</th>
                                        <th className="px-3 py-2 text-left">Nome</th>
                                        <th className="px-3 py-2 text-left">Telefone</th>
                                        <th className="px-3 py-2 text-left">Setor</th>
                                        <th className="px-3 py-2 text-left w-24">Situação</th>
                                        <th className="px-4 py-2 text-right w-36">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {usuariosDaEmpresa.length === 0 && (
                                        <tr><td colSpan="6"><Vazio>Nenhum usuário cadastrado para esta empresa. Use a linha verde abaixo para incluir.</Vazio></td></tr>
                                    )}
                                    {usuariosDaEmpresa.map((u) => (
                                        editUsuarioId === u.idFirebase ? (
                                            <tr key={u.idFirebase} className="bg-indigo-50">
                                                <td className="px-4 py-2 text-indigo-700 font-semibold">{u.nrUsuarioSequencial}</td>
                                                <td className="px-3 py-2">{campoUsuario(editUsuario.nome, v => setEditUsuario(p => ({ ...p, nome: v })), 'Nome', salvarEdicaoUsuario)}</td>
                                                <td className="px-3 py-2">{campoUsuario(editUsuario.telefone, v => setEditUsuario(p => ({ ...p, telefone: v })), 'Telefone', salvarEdicaoUsuario)}</td>
                                                <td className="px-3 py-2">{campoUsuario(editUsuario.setor, v => setEditUsuario(p => ({ ...p, setor: v })), 'Setor', salvarEdicaoUsuario)}</td>
                                                <td className="px-3 py-2">
                                                    <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none whitespace-nowrap" title="Desmarcado, o usuário não aparece para escolha em pendências novas">
                                                        <input type="checkbox" checked={editUsuario.ativo}
                                                            onChange={(e) => setEditUsuario(p => ({ ...p, ativo: e.target.checked }))}
                                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                                        Ativo
                                                    </label>
                                                </td>
                                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                                    <Botao tamanho="sm" variante="sucesso" disabled={salvandoUsuario} onClick={salvarEdicaoUsuario} className="mr-1">Salvar</Botao>
                                                    <Botao tamanho="sm" onClick={() => setEditUsuarioId(null)}>Cancelar</Botao>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={u.idFirebase} className={`hover:bg-gray-50 ${u.ativo === false ? 'opacity-60' : ''}`}>
                                                <td className="px-4 py-2 text-gray-400">{u.nrUsuarioSequencial}</td>
                                                <td className="px-3 py-2">
                                                    <span className="flex items-center gap-2 font-semibold text-gray-800"><Avatar nome={u.nome} className="h-7 w-7 text-[11px]" />{u.nome}</span>
                                                </td>
                                                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{u.telefone}</td>
                                                <td className="px-3 py-2 text-gray-600">{u.setor}</td>
                                                <td className="px-3 py-2">
                                                    {u.ativo === false
                                                        ? <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 bg-gray-200 rounded px-1.5 py-0.5">Inativo</span>
                                                        : <span className="text-[11px] font-semibold uppercase tracking-wide text-green-700 bg-green-100 rounded px-1.5 py-0.5">Ativo</span>}
                                                </td>
                                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                                    <Botao tamanho="sm" variante="fantasma" title="Editar" onClick={() => iniciarEdicaoUsuario(u)}><IconLapis /></Botao>
                                                    <Botao tamanho="sm" variante="fantasma" title="Excluir" className="text-red-500 hover:bg-red-50" onClick={() => excluirUsuario(u)}><IconLixo /></Botao>
                                                </td>
                                            </tr>
                                        )
                                    ))}
                                    <tr className="bg-green-50/70">
                                        <td className="px-4 py-2 text-xs text-green-700 font-bold uppercase">Novo</td>
                                        <td className="px-3 py-2">{campoUsuario(novoUsuario.nome, v => setNovoUsuario(p => ({ ...p, nome: v })), 'Nome *', incluirUsuario)}</td>
                                        <td className="px-3 py-2">{campoUsuario(novoUsuario.telefone, v => setNovoUsuario(p => ({ ...p, telefone: v })), 'Telefone *', incluirUsuario)}</td>
                                        <td className="px-3 py-2">{campoUsuario(novoUsuario.setor, v => setNovoUsuario(p => ({ ...p, setor: v })), 'Setor', incluirUsuario)}</td>
                                        <td className="px-3 py-2" />
                                        <td className="px-4 py-2 text-right">
                                            <Botao tamanho="sm" variante="sucesso" disabled={salvandoUsuario} onClick={incluirUsuario}><IconMais className="h-3.5 w-3.5" /> Incluir</Botao>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Card>
                ) : (
                    <Card
                        semPadding
                        titulo={`Empresas cadastradas (${filteredEmpresas.length})`}
                        acao={<Busca valor={searchTerm} onChange={(v) => setSearchTerm(v.toUpperCase())} placeholder="Nome, contato ou telefone" className="w-full sm:w-72" />}
                    >
                        {filteredEmpresas.length === 0 ? (
                            <Vazio>{empresas.length ? 'Nenhuma empresa encontrada.' : 'Nenhuma empresa cadastrada.'}</Vazio>
                        ) : (
                            <ul className="divide-y divide-gray-100 max-h-[calc(100vh-15rem)] overflow-y-auto">
                                {filteredEmpresas.map((company) => (
                                    <li key={company.idFirebase} onClick={() => setSelectedCompanyId(company.idFirebase)}
                                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 ${company.ativo === false ? 'opacity-60' : ''}`}>
                                        <span className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-green-50 text-green-700 flex-shrink-0"><IconPredio /></span>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-semibold text-gray-800 text-sm uppercase flex items-center gap-2 min-w-0">
                                                <span className="truncate">{company.nome}</span>
                                                {company.ativo === false && <span className="flex-shrink-0 text-[11px] font-semibold tracking-wide text-gray-600 bg-gray-200 rounded px-1.5 py-0.5">INATIVA</span>}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate">
                                                {company.contatoNome || 'Sem contato'}
                                                {company.contatoTelefone && <span className="inline-flex items-center gap-1 ml-2"><IconTelefone className="h-3 w-3" />{company.contatoTelefone}</span>}
                                            </div>
                                        </div>
                                        <span title="Usuários cadastrados" className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                                            <IconUsuarios className="h-3.5 w-3.5" />{usuariosPorEmpresa[company.idFirebase] || 0}
                                        </span>
                                        {company.nrEmpresaSequencial && <span className="text-xs text-gray-400 w-12 text-right hidden md:inline">Nr {company.nrEmpresaSequencial}</span>}
                                        <div className="flex" onClick={(e) => e.stopPropagation()}>
                                            <Botao tamanho="sm" variante="fantasma" title="Editar" onClick={() => setSelectedCompanyId(company.idFirebase)}><IconLapis /></Botao>
                                            <Botao tamanho="sm" variante="fantasma" title="Excluir" className="text-red-500 hover:bg-red-50" onClick={() => handleDeleteCompany(company.idFirebase)}><IconLixo /></Botao>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
};

export default EmpresaCadastro;
